import { BaseColor, Color, borderRadius, themeColor, useConstCallback } from '@unbounded/unbounded-components'
import { createWeb3Modal, defaultConfig } from '@web3modal/ethers'
import { BrowserProvider, ethers } from 'ethers'
import { useEffect, useRef, useState } from 'react'
import { useTheme } from 'styled-components'
import sleep from '~utils/sleep'

import { DEVNET_CHAIN, DEVNET_ZK_CHAIN, Web3ChainDefinition } from './consts'

interface Options {
  isAutoOpen?: boolean
  onCancel?: () => void
  chain?: Web3ChainDefinition
  zkChain?: Web3ChainDefinition
}

interface EthereumConnection {
  account: string
  provider: BrowserProvider
  zkProvider: BrowserProvider
}

export interface VestingInformation {
  index: number
  date: Date
  amount: string
  isClaimed: boolean
  isClaimable: boolean
}

export interface EthereumHookState {
  isLoading: boolean
  sign: (provider: BrowserProvider, bytes: string) => Promise<string>
  connection?: EthereumConnection
  open: () => void
  close: () => void
  queryBalance: (provider: BrowserProvider) => Promise<bigint>
  queryTokenAmount: (provider: BrowserProvider, tokenAddress: string) => Promise<string>
  queryTokenVesting: (provider: BrowserProvider, vestingAddress: string) => Promise<Array<VestingInformation>>
  sendTokens: (provider: BrowserProvider, tokenAddress: string, recipientAddress: string, amount: string) => Promise<string>
  bridgeL1TokensToL2: (
    provider: BrowserProvider,
    bridgeHubAddress: string,
    bridgeAddress: string,
    chainId: number,
    tokenAddress: string,
    recipient: string,
    amount: bigint
  ) => Promise<void>
  claimVestedTokens: (provider: BrowserProvider, vestingAddress: string, index: number) => Promise<void>
}

interface EthereumTransaction {
  from: string
  to: string
  data?: string
  gas?: string
  gasPrice?: string
}

function validateHex(value: string) {
  if (!value.match(/^0x[0-9A-Fa-f]*$/g)) {
    return false
  }

  if (value.length % 2 !== 0) {
    return false
  }

  return true
}

function validateHex256(num: string) {
  if (!num.match(/^0x[0-9A-Fa-f]*$/g)) {
    return false
  }

  if (num.length > 66) {
    return false
  }

  return true
}

function padHex256(num: string) {
  if (!validateHex256(num)) {
    return num
  }

  let output = '0x'

  for (let i = 0; i < 66 - num.length; i += 1) {
    output += '0'
  }

  output += num.substring(2)
  return output
}

function hexToUInt8Array(hex: string) {
  const bytes = []

  for (let i = 2; i < hex.length; i += 2) {
    bytes.push(Number.parseInt(hex.substring(i, i + 2), 16))
  }

  return new Uint8Array(bytes)
}

function parseIntArray(array: string): Array<string> {
  if (!validateHex(array)) {
    throw new Error(`Invalid int256 array (${array})`)
  }

  if (array === '0x') return []

  // layout:
  // 0x - 2 chars
  // [item type] - 64 chars, 256bit int (must be "2")
  // [item count] - 64 chars, 256bit int
  // [N x item] - 64 chars each, 256bit ints

  const itemType = array.slice(2, 2 + 64)
  const itemCount = array.slice(2 + 64, 2 + 64 + 64)

  const itemTypeInt = BigInt(`0x${itemType}`)
  const itemCountInt = BigInt(`0x${itemCount}`)

  if (itemTypeInt !== 32n) {
    throw new Error(`Invalid int256 array (type ${itemTypeInt})`)
  }

  const result: Array<string> = []

  for (let i = 0; i < Number(itemCountInt); i += 1) {
    result.push(`0x${array.slice(2 + 64 + 64 + i * 64, 2 + 64 + 64 + i * 64 + 64)}`)
  }

  return result
}

const useEthereumConnection = (opts?: Options): EthereumHookState => {
  const theme = useTheme()

  const isConnected = useRef(false)

  const [modal, setModal] = useState<ReturnType<typeof createWeb3Modal> | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [connection, setConnection] = useState<EthereumConnection | undefined>(undefined)

  const checkOpen = useConstCallback(async (newModal: ReturnType<typeof createWeb3Modal>) => {
    if (opts?.isAutoOpen) {
      await newModal.open()
    }
  })

  const checkCancel = useConstCallback(() => {
    if (!isConnected.current && !connection && !modal?.getIsConnected()) {
      opts?.onCancel?.()
    }
  })

  const loadModal = useConstCallback(() => {
    const chain = opts?.chain || DEVNET_CHAIN
    const zkChain = opts?.zkChain || DEVNET_ZK_CHAIN

    const metadata = {
      name: 'Identity Manager',
      description: 'Single Sign-On Service',
      url: window.location.origin,
      icons: [] as Array<any>,
    }

    const ethersConfig = defaultConfig({
      metadata,
      defaultChainId: chain.chainId,
    })

    // hack. enforces cleanup of previous session
    Object.keys(window.localStorage)
      .filter(k => k.startsWith('@w3m/'))
      .forEach(k => delete window.localStorage[k])

    const newModal = createWeb3Modal({
      ethersConfig,
      chains: [chain],
      projectId: '1b2dcdc4814f62b1ebc75e61e717bba8',
      enableAnalytics: false, // Optional - defaults to your Cloud configuration
    })

    newModal.setThemeMode('light')
    newModal.setThemeVariables({
      '--w3m-color-mix': themeColor(BaseColor.surfaceBg)({ theme }),
      '--w3m-color-mix-strength': 40,
      '--w3m-accent': themeColor(Color.primary)({ theme }),
      '--w3m-border-radius-master': borderRadius({ theme })({ theme }),
    })

    newModal.subscribeProvider(state => {
      if (state.isConnected && state.address) {
        setConnection({
          account: state.address,
          provider: new BrowserProvider(newModal.getWalletProvider()!, chain),
          zkProvider: new BrowserProvider(newModal.getWalletProvider()!, zkChain),
        })
        isConnected.current = true
      } else {
        setConnection(undefined)
      }
    })

    newModal.subscribeEvents(event => {
      if (event.data?.event === 'CONNECT_SUCCESS') {
        isConnected.current = true
      } else if (event.data?.event === 'MODAL_CLOSE') {
        checkCancel()
      }
    })

    checkOpen(newModal)

    setModal(newModal)
    return newModal
  })

  useEffect(() => {
    const newModal = loadModal()

    return () => {
      newModal.disconnect()
      newModal.close()
    }
  }, [theme, opts?.chain, opts?.zkChain])

  const open = useConstCallback(async () => {
    setIsLoading(true)
    await modal?.open()
    setIsLoading(false)
  })

  const close = useConstCallback(async () => {
    setIsLoading(true)
    await modal?.close()
    setIsLoading(false)
  })

  const sign = useConstCallback(async (provider: BrowserProvider, data: string) => {
    if (!connection) {
      throw new Error(`Failed to sign: not connected to Ethereum.`)
    }

    try {
      return await (await provider.getSigner(connection.account)).signMessage(hexToUInt8Array(data))
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('WalletConnect error', e)
      throw new Error('Failed or refused to sign authorization request.')
    }
  })

  const queryBalance = useConstCallback(async (provider: BrowserProvider) => {
    if (!connection) {
      throw new Error('Not connected')
    }

    // hack. this is because the provider does not really give us information about the chain URL.
    // we are guessing based on provider instance
    if (provider !== connection.provider && provider !== connection.zkProvider) {
      throw new Error('Provider not supported')
    }

    const chain: Web3ChainDefinition | undefined = provider === connection.provider ? opts?.chain : opts?.zkChain

    if (!chain) {
      throw new Error('Unknown chain')
    }

    const result = await fetch(chain.rpcUrl, {
      method: 'POST',
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'eth_getBalance',
        params: [connection.account, 'latest'],
        id: 1,
      }),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })

    const jsonResult = await result.json()

    if (typeof jsonResult.result !== 'string') {
      throw new Error('JSON-RPC failed')
    }

    return BigInt(jsonResult.result)
  })

  const requestCall = useConstCallback(async (provider: BrowserProvider, callParam: string, to: string): Promise<string> => {
    if (!connection) {
      throw new Error(`Failed to call: not connected to Ethereum.`)
    }

    return provider.call({ data: callParam, to })
  }) as <T>(provider: BrowserProvider, callParam: string, to: string) => Promise<T>

  const queryTokenAmount = useConstCallback(async (provider: BrowserProvider, address: string) => {
    if (!connection) {
      throw new Error(`Failed to query: not connected to Ethereum.`)
    }

    if (address.length !== 42 || !validateHex256(address)) {
      throw new Error(`Failed to query: contract address is invalid.`)
    }

    // ABI for balanceOf():
    // https://www.quicknode.com/guides/ethereum-development/how-to-look-up-wallet-balance-of-an-erc20-token-using-rpc-api
    const callParam = `0x70a08231${padHex256(connection.account).substring(2).toLowerCase()}`

    return requestCall<string>(provider, callParam, address)
  })

  const queryTokenVesting = useConstCallback(async (provider: BrowserProvider, address: string) => {
    if (!connection) {
      throw new Error(`Failed to query: not connected to Ethereum.`)
    }

    if (address.length !== 42 || !validateHex256(address)) {
      throw new Error(`Failed to query: contract address is invalid.`)
    }

    // https://piyolab.github.io/playground/ethereum/getEncodedFunctionSignature/
    // signature: getReleaseStatus(address)
    const callParamForStatus = `0xc277c80f${padHex256(connection.account).substring(2).toLowerCase()}`
    // signature: releaseDates()
    const callParamForDates = `0xc00de5fc`
    // signature: releaseAmount()
    const callParamForAmount = `0xc062dc5f`

    const results = await Promise.all([
      requestCall<string>(provider, callParamForStatus, address),
      requestCall<string>(provider, callParamForDates, address),
      requestCall<string>(provider, callParamForAmount, address),
    ])

    const statuses = parseIntArray(results[0]).map(x => Number.parseInt(x, 16))
    const dates = parseIntArray(results[1]).map(x => new Date(Number.parseInt(x, 16) * 1000))

    const info: Array<VestingInformation> = []

    if (statuses.length !== dates.length) {
      return []
    }

    for (let i = 0; i < dates.length; i += 1) {
      info.push({
        date: dates[i],
        index: i,
        amount: results[2],
        isClaimable: statuses[i] === 3,
        isClaimed: statuses[i] === 1,
      })
    }

    return info
  })

  const getTransactionReceipt = useConstCallback(async (provider: BrowserProvider, txid: string) => {
    if (!connection) {
      throw new Error(`Failed to query receipt: not connected to Ethereum.`)
    }

    return provider.getTransactionReceipt(txid)
  })

  const sendTransaction = useConstCallback(async (provider: BrowserProvider, transactionTemplate: EthereumTransaction) => {
    if (!connection) {
      throw new Error(`Failed to send transaction: not connected to Ethereum.`)
    }

    const transaction = { ...transactionTemplate }

    return (await (await provider.getSigner(connection.account)).sendTransaction(transaction)).hash
  })

  const claimVestedTokens = useConstCallback(async (provider: BrowserProvider, address: string, index: number) => {
    if (!connection) {
      throw new Error(`Failed to claim: not connected to Ethereum.`)
    }

    if (address.length !== 42 || !validateHex256(address)) {
      throw new Error(`Failed to claim: contract address is invalid.`)
    }

    // signature: claimTokens(uint256)
    const callParam = `0x46e04a2f${padHex256(`0x${index.toString(16)}`).slice(2)}`

    const transaction: EthereumTransaction = {
      from: connection.account,
      to: address,
      data: callParam,
      gas: `0x${(200000).toString(16)}`,
    }

    const transactionHash = await sendTransaction(provider, transaction)

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const receipt = await getTransactionReceipt(provider, transactionHash)

      if (receipt) {
        break
      }

      // eslint-disable-next-line no-await-in-loop
      await sleep(2500)
    }
  })

  const sendTokens = useConstCallback(async (provider: BrowserProvider, address: string, recipient: string, amount: string) => {
    if (!connection) {
      throw new Error(`Failed to send: not connected to Ethereum.`)
    }

    if (address.length !== 42 || !validateHex256(address)) {
      throw new Error(`Failed to send: contract address is invalid.`)
    }

    if (recipient.length !== 42 || !validateHex256(recipient)) {
      throw new Error(`Failed to send: recipient address is invalid.`)
    }

    if (!validateHex256(amount)) {
      throw new Error(`Failed to send: amount is invalid.`)
    }

    // ABI for transfer():
    // https://goethereumbook.org/en/transfer-tokens/
    const callParam = `0xa9059cbb${padHex256(recipient).substring(2).toLowerCase()}${padHex256(amount).substring(2).toLowerCase()}`

    const transaction: EthereumTransaction = {
      from: connection.account,
      to: address,
      data: callParam,
      gas: `0x${(75000).toString(16)}`,
    }

    return sendTransaction(provider, transaction)
  })

  const bridgeL1TokensToL2 = useConstCallback(
    async (
      provider: BrowserProvider,
      bridgeHubAddress: string,
      bridgeAddress: string,
      chainId: number,
      tokenAddress: string,
      recipient: string,
      amount: bigint
    ) => {
      if (!connection) {
        throw new Error(`Failed to send: not connected to Ethereum.`)
      }

      if (tokenAddress.length !== 42 || !validateHex256(tokenAddress)) {
        throw new Error(`Failed to send: contract address is invalid.`)
      }

      if (bridgeHubAddress.length !== 42 || !validateHex256(bridgeHubAddress)) {
        throw new Error(`Failed to send: bridge address is invalid.`)
      }

      if (recipient.length !== 42 || !validateHex256(recipient)) {
        throw new Error(`Failed to send: recipient address is invalid.`)
      }

      const tokenABI = [
        'function allowance(address owner, address spender) public view returns (uint256)',
        'function approve(address spender, uint256 value) public returns (bool)',
      ]

      // this is necessary because Ethers ABI parser does not support structs.
      // we use tuples instead.
      // https://github.com/ethers-io/ethers.js/issues/315
      const L2TransactionRequestDirect = `
      (
        uint256 chainId,
        uint256 mintValue,
        address l2Contract,
        uint256 l2Value,
        bytes l2Calldata,
        uint256 l2GasLimit,
        uint256 l2GasPerPubdataByteLimit,
        bytes[] factoryDeps,
        address refundRecipient,
      )
    `

      const bridgeHubABI = [
        `function requestL2TransactionDirect(${L2TransactionRequestDirect} calldata _request) external payable returns (bytes32 canonicalTxHash)`,
        'function l2TransactionBaseCost(uint256 _chainId, uint256 _gasPrice, uint256 _l2GasLimit, uint256 _l2GasPerPubdataByteLimit) external view returns (uint256)',
      ]

      // step 1: check if there is allowance between token and bridgehub
      // step 2 (if not allowed/not enough): if not, issue a call to token contract allow token use by bridgehub (ERC20 approve)
      // step 3: calculate L2 transaction base cost from bridgehub
      // step 4: issue a call to bridgehub to mint tokens on L2 chain by transferring L1 tokens

      const signer = await provider.getSigner(recipient)

      // step 1
      const tokenContract = new ethers.Contract(tokenAddress, tokenABI, signer)
      const allowance = await tokenContract.allowance(recipient, bridgeAddress)

      if (allowance < amount) {
        // step 2
        // we request for max allowance (unlimited) so that we don't have to re-ask it later
        const MAX_SIGNED_INT256 = '0x7fffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'
        const tx = await tokenContract.approve(bridgeAddress, BigInt(MAX_SIGNED_INT256))

        await tx.wait()
      }

      // step 3
      const REQUIRED_L1_TO_L2_GAS_PER_PUBDATA_LIMIT = 800
      const DEFAULT_GAS_PER_PUBDATA_LIMIT = 50000
      const DEPOSIT_L2_GAS_LIMIT = 10000000
      const feeData = await provider.getFeeData()
      const bridgeHubContract = new ethers.Contract(bridgeHubAddress, bridgeHubABI, signer)
      const expectedCost =
        ((await bridgeHubContract.l2TransactionBaseCost(chainId, feeData.gasPrice, DEPOSIT_L2_GAS_LIMIT, DEFAULT_GAS_PER_PUBDATA_LIMIT)) * 150n) /
        100n

      // step 4
      const balanceBeforeBridge = await queryBalance(connection.zkProvider)

      const l2CallData = '0x'
      const tx = await bridgeHubContract.requestL2TransactionDirect([
        chainId,
        amount + expectedCost,
        recipient,
        amount,
        l2CallData,
        DEPOSIT_L2_GAS_LIMIT,
        REQUIRED_L1_TO_L2_GAS_PER_PUBDATA_LIMIT,
        [],
        recipient,
      ])

      await tx.wait()

      // wait until the L2 balance changes in any way
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        if ((await queryBalance(connection.zkProvider)) !== balanceBeforeBridge) {
          break
        }
        // eslint-disable-next-line no-await-in-loop
        await sleep(5000)
      }
    }
  )

  return {
    isLoading,
    connection,
    sign,
    open,
    close,
    queryBalance,
    queryTokenAmount,
    queryTokenVesting,
    sendTokens,
    bridgeL1TokensToL2,
    claimVestedTokens,
  }
}

export default useEthereumConnection
